import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  WhatsApp as WhatsAppIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Info as InfoIcon,
  Router as RouterIcon,
} from '@mui/icons-material';
import KangarooIcon from '../icons/KangarooIcon';
import { GatewayWhatsapp, GatewayWhatsappFormData } from '../../types';
import { gatewayWhatsappService } from '../../services/gatewayWhatsappService';
import { atendimentoService, AtendimentoConexao } from '../../services/atendimentoService';

type GatewayWhatsappType = 'whaticket' | 'kangaroo' | 'local';

interface GatewayWhatsappFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (gatewayData: GatewayWhatsappFormData) => Promise<void>;
  gateway?: GatewayWhatsapp | null;
  loading?: boolean;
}

interface FormErrors {
  nome?: string;
  tipo?: string;
  token_whaticket?: string;
  token_kangaroo?: string;
  conexao_id?: string;
  ticket_status_criacao?: string;
  webhook_url?: string;
}

const initialFormData: GatewayWhatsappFormData = {
  nome: '',
  tipo: 'whaticket',
  ativo: true,
  is_default: false,
  token_whaticket: '',
  token_kangaroo: '',
  conexao_id: undefined,
  criar_ticket: false,
  ticket_status_criacao: 'pendente',
  observacoes: '',
};

const GatewayWhatsappForm: React.FC<GatewayWhatsappFormProps> = ({
  open,
  onClose,
  onSubmit,
  gateway,
  loading = false,
}) => {
  const [formData, setFormData] = useState<GatewayWhatsappFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [conexoes, setConexoes] = useState<AtendimentoConexao[]>([]);

  const isEditing = !!gateway;

  // Carregar dados do gateway para edição
  useEffect(() => {
    if (isEditing && gateway) {
      setFormData({
        nome: gateway.nome,
        tipo: gateway.tipo,
        ativo: gateway.ativo,
        is_default: gateway.is_default,
        
        // Campos Whaticket
        token_whaticket: gateway.token_whaticket || '',
        
        // Campos Kangaroo
        token_kangaroo: gateway.token_kangaroo || '',

        // Campo conexao local
        conexao_id: gateway.conexao_id || undefined,
        criar_ticket: gateway.criar_ticket ?? false,
        ticket_status_criacao: gateway.ticket_status_criacao || 'pendente',
        
        // Campos comuns
        webhook_url: gateway.webhook_url || '',
        observacoes: gateway.observacoes || '',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setSubmitError('');
  }, [gateway, isEditing, open]);

  // Carregar conexões nativas do atendimento
  useEffect(() => {
    const fetchConexoes = async () => {
      try {
        const response = await atendimentoService.getConexoes();
        // Filtrar apenas as conexões de WhatsApp ativas
        const activeWhatsappConns = (response || []).filter((c: any) => c.ativo && c.tipo === 'whatsapp');
        setConexoes(activeWhatsappConns);
      } catch (error) {
        console.error('Erro ao carregar conexões locais:', error);
      }
    };

    if (open) {
      fetchConexoes();
    }
  }, [open]);

  const handleInputChange = (field: keyof GatewayWhatsappFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field as keyof FormErrors]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof GatewayWhatsappFormData) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar campos específicos ao mudar tipo
    if (field === 'tipo') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        // Limpar campos específicos dos outros tipos
        token_whaticket: '',
        token_kangaroo: '',
        conexao_id: undefined,
        criar_ticket: false,
        ticket_status_criacao: 'pendente',
      }));
    }
    
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field as keyof FormErrors]: undefined }));
    }
  };

  const handleSwitchChange = (field: keyof GatewayWhatsappFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setFormData(prev => ({
      ...prev,
      [field]: checked,
      ...(field === 'criar_ticket' && checked && !prev.ticket_status_criacao
        ? { ticket_status_criacao: 'pendente' as const }
        : {}),
    }));
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validações básicas
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    // Validações específicas por tipo
    switch (formData.tipo) {
      case 'whaticket':
        if (!formData.token_whaticket?.trim()) {
          newErrors.token_whaticket = 'Token é obrigatório';
        }
        if (!formData.webhook_url?.trim()) {
          newErrors.webhook_url = 'Webhook URL é obrigatório';
        }
        break;
      
      case 'kangaroo':
        if (!formData.token_kangaroo?.trim()) {
          newErrors.token_kangaroo = 'Token é obrigatório';
        }
        break;
      
      case 'local':
        if (!formData.conexao_id) {
          newErrors.conexao_id = 'Conexão local é obrigatória';
        }
        if (formData.criar_ticket && !formData.ticket_status_criacao) {
          newErrors.ticket_status_criacao = 'Selecione o status do ticket';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error: any) {
      console.error('Erro no formulário:', error);
      setSubmitError(error.message || 'Erro ao salvar gateway WhatsApp');
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitError('');
    setShowPasswords({});
    onClose();
  };

  // Renderizar campos de configuração específicos por tipo
  const renderConfigurationFields = () => {
    switch (formData.tipo) {
      case 'whaticket':
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <WhatsAppIcon sx={{ mr: 1 }} />
              Configurações Whaticket
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token"
                  value={formData.token_whaticket || ''}
                  onChange={handleInputChange('token_whaticket')}
                  error={!!errors.token_whaticket}
                  helperText={errors.token_whaticket}
                  required
                  type={showPasswords.token_whaticket ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('token_whaticket')}
                          edge="end"
                        >
                          {showPasswords.token_whaticket ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL*"
                  value={formData.webhook_url || ''}
                  onChange={handleInputChange('webhook_url')}
                  error={!!errors.webhook_url}
                  helperText={errors.webhook_url}
                  required
                  placeholder="https://meusite.com/webhook/whatsapp"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'kangaroo':
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <WhatsAppIcon sx={{ mr: 1 }} />
              Configurações Kangaroo
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token"
                  value={formData.token_kangaroo || ''}
                  onChange={handleInputChange('token_kangaroo')}
                  error={!!errors.token_kangaroo}
                  helperText={errors.token_kangaroo}
                  required
                  type={showPasswords.token_kangaroo ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('token_kangaroo')}
                          edge="end"
                        >
                          {showPasswords.token_kangaroo ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'local':
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <RouterIcon sx={{ mr: 1 }} />
              Conexão Local (Atendimento)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.conexao_id} required>
                  <InputLabel>Selecione a Conexão *</InputLabel>
                  <Select
                    value={formData.conexao_id || ''}
                    onChange={handleSelectChange('conexao_id')}
                    label="Selecione a Conexão *"
                  >
                    {conexoes.length > 0 ? (
                      conexoes.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.nome} ({c.numero || 'Sem número'}) - Status: {c.status}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled value="">
                        Nenhuma conexão nativa de WhatsApp ativa encontrada
                      </MenuItem>
                    )}
                  </Select>
                  {errors.conexao_id && (
                    <Typography variant="caption" color="error">
                      {errors.conexao_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.criar_ticket}
                      onChange={handleSwitchChange('criar_ticket')}
                      color="primary"
                    />
                  }
                  label="Criar ticket no atendimento ao enviar mensagem"
                />
              </Grid>

              {formData.criar_ticket && (
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.ticket_status_criacao} required>
                    <InputLabel>Status do ticket *</InputLabel>
                    <Select
                      value={formData.ticket_status_criacao || 'pendente'}
                      onChange={handleSelectChange('ticket_status_criacao')}
                      label="Status do ticket *"
                    >
                      <MenuItem value="pendente">Pendente</MenuItem>
                      <MenuItem value="fechado">Fechado</MenuItem>
                    </Select>
                    {errors.ticket_status_criacao && (
                      <Typography variant="caption" color="error">
                        {errors.ticket_status_criacao}
                      </Typography>
                    )}
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Se já existir ticket aberto para o contato, ele será reutilizado e a mensagem será registrada nele.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WhatsAppIcon />
          {isEditing ? 'Editar Gateway WhatsApp' : 'Novo Gateway WhatsApp'}
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {/* Campos básicos */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Nome do Gateway"
                value={formData.nome}
                onChange={handleInputChange('nome')}
                error={!!errors.nome}
                helperText={errors.nome}
                required
                placeholder="Ex: Gateway WhatsApp Principal"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!errors.tipo}>
                <InputLabel>Tipo *</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={handleSelectChange('tipo')}
                  label="Tipo *"
                >
                  <MenuItem value="whaticket">
                    <Box display="flex" alignItems="center" gap={1}>
                      💬 Whaticket
                    </Box>
                  </MenuItem>
                  <MenuItem value="kangaroo">
                    <Box display="flex" alignItems="center" gap={1}>
                      <KangarooIcon sx={{ width: 20, height: 20 }} />
                      Kangaroo
                    </Box>
                  </MenuItem>
                  <MenuItem value="local">
                    <Box display="flex" alignItems="center" gap={1}>
                      <RouterIcon sx={{ width: 20, height: 20 }} />
                      Local (Conexão Nativa)
                    </Box>
                  </MenuItem>
                </Select>
                {errors.tipo && (
                  <Typography variant="caption" color="error">
                    {errors.tipo}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ativo}
                    onChange={handleSwitchChange('ativo')}
                    color="primary"
                  />
                }
                label="Gateway Ativo"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={handleSwitchChange('is_default')}
                    color="warning"
                    icon={<StarBorderIcon />}
                    checkedIcon={<StarIcon />}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Gateway Padrão
                    <Chip 
                      size="small" 
                      label="DEFAULT" 
                      color="warning" 
                      style={{ display: formData.is_default ? 'inline-flex' : 'none' }}
                    />
                  </Box>
                }
              />
            </Grid>
          </Grid>

          {/* Campos específicos por tipo */}
          {renderConfigurationFields()}

          {/* Campos opcionais */}
          <Box mt={3}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1 }} />
              Configurações Opcionais
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>           

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações"
                  value={formData.observacoes || ''}
                  onChange={handleInputChange('observacoes')}
                  multiline
                  rows={3}
                  placeholder="Observações sobre este gateway..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<WhatsAppIcon />}
          >
            {loading ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GatewayWhatsappForm;
